import get from 'lodash/get';

import {
  ActivateTransactionFragment,
  TransactionDetailsFragment,
  TransactionFragment,
} from '@occc/graphql';

import { ActivateTransactionViewModel } from '@occc-app/dataAccessLayer/activateTransaction/queries';

import {
  PurchaseType,
  TransactionViewFinalizeRequestData,
  TransactionViewItemData,
  TransactionViewItemSavingData,
} from '@occc-app/routes/Transactions/TransactionView/types';

import { displayAmount } from '@occc-app/lib/currency';
import { NO_DATA_PLACEHOLDER } from '@occc-app/design-system';
import { routeCreator as awardRouteCreator } from '@occc-app/routes/AwardCatalog/utils';

import {
  activateTransactionStatusLabelMap,
  orderAheadVendorNames,
} from './transaction-view.constants';

import { routeCreator } from '../utils';

export const TRANSACTION_DISCOUNT = 'transaction_discount';

type ActivateTransactionPointChange = NonNullable<
  ActivateTransactionViewModel['pointTransactions'][number]
>;

const getPointChangeDescription = (pointChange: ActivateTransactionPointChange): string =>
  pointChange.offer?.marketingContents?.[0]?.title?.trim() ||
  pointChange.offer?.name ||
  pointChange.pointAdjustmentReason?.label ||
  pointChange.notes?.trim() ||
  'Points';

const mapPointChangeToSaving = (
  pointChange: ActivateTransactionPointChange,
): TransactionViewItemSavingData => {
  // EAP award/voucher purchases arrive as OfferPurchase-sourced point changes — surface
  // the awarded voucher's name and link to its Award Catalog detail page.
  if (pointChange.source?.__typename === 'OfferPurchase') {
    const award = pointChange.source.voucherGrantFor;
    return {
      type: 'points' as const,
      amount: pointChange.quantity,
      description: award.marketingContents?.[0]?.title?.trim() || award.name,
      href: awardRouteCreator.details(award.id),
    };
  }

  const offerId = pointChange.offer?.id;
  const campaignId = pointChange.offer?.campaign?.uuid;

  return {
    type: 'points' as const,
    amount: pointChange.quantity,
    description: getPointChangeDescription(pointChange),
    href:
      campaignId && offerId ? routeCreator.campaignOfferDetails(campaignId, offerId) : undefined,
  };
};

// Only collapse a repeated saving once it occurs this many times — a handful of identical
// rows is readable, but per-unit awards (e.g. points per gallon) can produce dozens.
const SAVINGS_GROUPING_THRESHOLD = 5;

const getSavingKey = (saving: TransactionViewItemSavingData): string =>
  [saving.type, saving.description, saving.href ?? '', saving.isExternal ? 1 : 0].join('|');

// Collapse identical savings (same description, link, and type) into a single row that
// carries the occurrence `count` and summed `amount`, keeping the individual savings in
// `items` so the row can be expanded. Only groups keys that repeat at least
// SAVINGS_GROUPING_THRESHOLD times; anything below that passes through in its original order.
const groupIdenticalSavings = (
  savings: TransactionViewItemSavingData[],
): TransactionViewItemSavingData[] => {
  const itemsByKey = new Map<string, TransactionViewItemSavingData[]>();
  savings.forEach((saving) => {
    const key = getSavingKey(saving);
    const items = itemsByKey.get(key);
    if (items) items.push(saving);
    else itemsByKey.set(key, [saving]);
  });

  const emittedGroupKeys = new Set<string>();
  const result: TransactionViewItemSavingData[] = [];

  savings.forEach((saving) => {
    const key = getSavingKey(saving);
    const items = itemsByKey.get(key) ?? [saving];

    if (items.length < SAVINGS_GROUPING_THRESHOLD) {
      result.push(saving);
      return;
    }

    if (emittedGroupKeys.has(key)) return;
    emittedGroupKeys.add(key);

    result.push({
      ...saving,
      amount: items.reduce((sum, item) => sum + item.amount, 0),
      count: items.length,
      items,
    });
  });

  return result;
};

export const getPointChangesSaving = (
  pointTransactions?: ActivateTransactionViewModel['pointTransactions'] | null,
): TransactionViewItemSavingData[] =>
  groupIdenticalSavings((pointTransactions ?? []).map(mapPointChangeToSaving));

export const getActivateTransactionLocationAddress = (
  location: ActivateTransactionFragment['location'],
): string => {
  const { address1 = '', address2 = '', city = '', state = '', zipcode = '' } = location || {};
  return (
    [address1, address2, city, state, zipcode].filter(Boolean).join(', ') || NO_DATA_PLACEHOLDER
  );
};

export const getTransactionDetailsSiteId = (
  location: TransactionDetailsFragment['location'],
): string | undefined =>
  location?.siteSystemConfiguration
    ?.map((value) => value.identifier)
    .filter(Boolean)
    .join(' - ');

export const getTransactionDetailsLocationAddress = (
  location: TransactionDetailsFragment['location'],
): string => {
  const { street1 = '', city = '', state = '', zipCode = '' } = location?.address || {};
  return [street1, city, state, zipCode].filter(Boolean).join(', ') || NO_DATA_PLACEHOLDER;
};

export const getTransactionDetailsTotal = (items: TransactionDetailsFragment['items']): number =>
  items ? +items.reduce((acc, item) => acc + (item?.amount?.price || 0), 0).toFixed(2) : 0;

export const getActivateTransactionTotal = (
  items: ActivateTransactionFragment['lineItems'],
): number => +items.reduce((acc, item) => acc + (item.moneyAmount || 0), 0).toFixed(2);

export const getActivateTransactionTotalBeforeDiscount = (
  items: ActivateTransactionFragment['lineItems'],
): number =>
  +items
    .reduce((acc, item) => acc + (item.regularSellPrice || 0) * (item.quantity || 1), 0)
    .toFixed(2);

export const getTransactionDetailsPayWith = (
  paymentInfo: TransactionDetailsFragment['paymentInfo'],
): string =>
  (paymentInfo || [])
    .map((info) => {
      return info?.cardCircuit
        ? info.cardCircuit
        : /na|other/i.test(info?.paymentMethod || '')
        ? 'Unknown'
        : info?.paymentMethod;
    })
    .filter(Boolean)
    .join(', ') || 'Unknown';

export const getActivateTransactionPayWith = (
  paymentInfos: ActivateTransactionFragment['paymentInfos'],
): string =>
  [...(paymentInfos || [])]
    .sort((info) => (info.tenderSubtype ? -1 : 1)) // Show known items first
    .map((info) => {
      return info.tenderSubtype
        ? info.tenderSubtype
        : /card/i.test(info.tenderType || '')
        ? 'Card'
        : info.tenderType;
    })
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i) // Unique values
    .join(', ') || 'Unknown';

export const getActivateTransactionPromotionsItems = (
  promotions: ActivateTransactionFragment['lineItems'][0]['promotions'],
  externalPromotion?: ActivateTransactionFragment['lineItems'][0]['externalLineItemPromotions'],
): TransactionViewItemSavingData[] => {
  const promotionsList = (promotions || []).map((value) => {
    const amount = Math.abs(value?.promotionAmount) || 0;
    const offerId = value?.offer?.id;
    const campaignId = value?.offer?.campaign?.uuid;
    return {
      href:
        campaignId && offerId ? routeCreator.campaignOfferDetails(campaignId, offerId) : undefined,
      description: value?.promotionTitle || `${displayAmount(amount, false)} Discount`,
      amount,
      type: 'money' as const,
    };
  });

  const externalPromotionList = (externalPromotion || []).map((value) => {
    return {
      description: 'Other Savings',
      amount: value?.discountAmount || 0,
      type: 'money' as const,
      isExternal: true,
    };
  });

  return [...promotionsList, ...externalPromotionList];
};

export const getActivateTransactionItems = (
  items: ActivateTransactionFragment['lineItems'],
): TransactionViewItemData[] => {
  return items
    .map<TransactionViewItemData>((item) => {
      const {
        id,
        group,
        sku,
        upc,
        description,
        quantity,
        moneyAmount = 0,
        moneyAmountWithoutDiscounts,
        promotions,
        externalLineItemPromotions,
        fuelGradeId,
      } = item;

      let name;
      let savings;
      const amount = moneyAmount || 0;
      const altDiscountName = `${displayAmount(Math.abs(amount), false)} Discount`;

      if (
        group === TRANSACTION_DISCOUNT &&
        !promotions?.length &&
        !externalLineItemPromotions?.length
      ) {
        name = '';
        savings = [
          {
            isExternal: true,
            description: description || altDiscountName,
            amount,
            type: 'money' as const,
          },
        ];
      } else {
        name =
          group === TRANSACTION_DISCOUNT
            ? description || altDiscountName
            : description || sku || '';
        savings = getActivateTransactionPromotionsItems(promotions, externalLineItemPromotions);
      }

      return {
        id,
        name,
        savings: savings ?? [],
        group: group ?? undefined,
        sku: sku ?? undefined,
        upc: upc ?? undefined,
        quantity: quantity || 0,
        quantityUnit: fuelGradeId ? 'gal' : undefined,
        moneyAmount:
          group === TRANSACTION_DISCOUNT ? amount : moneyAmountWithoutDiscounts ?? amount,
      };
    })
    .sort(
      (a, b) => Number(a.group === TRANSACTION_DISCOUNT) - Number(b.group === TRANSACTION_DISCOUNT), // transaction_discount should be at the end
    );
};

export const getTransactionDetailsItems = (
  items: TransactionDetailsFragment['items'],
  fuelReceiptProduct?: string,
): TransactionViewItemData[] => {
  return (items || []).filter(Boolean).map<TransactionViewItemData>((item, index) => {
    const savings = [];
    if (item?.discountApplied?.price) {
      savings.push({
        description:
          item.discountDescription ||
          `${displayAmount(item.discountApplied.price, false)} Discount`,
        amount: item.discountApplied.price,
        type: 'money' as const,
      });
    }
    return {
      id: String(index),
      name: item?.itemDescription || '',
      quantity: item?.qty || 1,
      quantityUnit: fuelReceiptProduct === item?.itemDescription ? 'gal' : undefined,
      moneyAmount: item?.amount?.price || 0,
      savings,
    };
  });
};

export const getTransactionDetailsTotalDiscount = (
  items: TransactionDetailsFragment['items'],
): number => {
  return (items || [])
    .filter(Boolean)
    .reduce(
      (sum, item) =>
        sum +
        (item?.discountDescription && item.discountApplied?.price
          ? Math.abs(item.discountApplied.price)
          : 0),
      0,
    );
};

export const getTransactionAddressWithLocationId = (transaction: TransactionFragment) => {
  const brandSpecificLocationId =
    transaction.location?.siteSystemConfiguration?.[0]?.identifier || '';
  const locationCity = transaction.location?.address?.city;
  const locationState = transaction.location?.address?.state;
  const address = locationState && locationCity ? ` - ${locationCity}, ${locationState}` : '';
  return `${brandSpecificLocationId}${address}`;
};

export const getIsOrderAheadTransaction = (
  vendorName: ActivateTransactionFragment['vendorName'],
): boolean => {
  // Add any additional vendor names that should be considered order-ahead transactions
  return !!(vendorName && orderAheadVendorNames.includes(vendorName.toLowerCase()));
};

export const getTransactionPurchaseType = (
  transaction: ActivateTransactionFragment,
): PurchaseType | null => {
  if (getIsOrderAheadTransaction(transaction.vendorName)) return 'order-ahead';
  return null;
};

export const getActivateTransactionStatusLabel = (
  status: ActivateTransactionFragment['status'],
): string | null => {
  if (!status) return null;
  return activateTransactionStatusLabelMap[status];
};

export const getTransactionViewFinalizeRequestData = (
  transaction: ActivateTransactionFragment,
): TransactionViewFinalizeRequestData => {
  const joinUnique = (values: Array<string | null | undefined>): string =>
    [...new Set(values.filter(Boolean) as string[])].join(', ');

  const payload = transaction.payload;
  const fuelPrepay = get(payload, 'messageData.fuelPrepayFlag');
  const outsideSales = get(payload, 'messageData.outsideSalesFlag');

  return {
    loyaltySequenceId:
      get(payload, 'messageData.loyaltySequenceId') || (transaction.loyaltySequenceId ?? undefined),
    posType: get(payload, 'messageData.vendorName') || (transaction.vendorName ?? undefined),
    posSequenceId: get(payload, 'rawConexxusMessage.uniqueId'),
    vendorModel: get(payload, 'messageData.vendorModelVersion'),
    interfaceVersion: get(payload, 'rawConexxusMessage.typename'),
    entryMethod: joinUnique(
      get(payload, 'messageData.transactionLineItems', []).map(
        (v: { entryMethod: string }) => v.entryMethod,
      ),
    ),
    host: get(payload, 'host.status'),
    sale: outsideSales === true ? 'Outside' : outsideSales === false ? 'Inside' : 'N/A',
    fuelPrepay: fuelPrepay === true ? 'Yes' : fuelPrepay === false ? 'No' : 'N/A',
    tenderCode: joinUnique([
      ...get(payload, 'messageData.tenderInfo', []).map(
        (v: { tenderCode: string }) => v.tenderCode,
      ),
      ...(transaction?.paymentInfos || []).map((v) => v.tenderType),
    ]),
    tenderSubcode: joinUnique([
      ...get(payload, 'messageData.tenderInfo', []).map(
        (v: { tenderSubCode: string }) => v.tenderSubCode,
      ),
      ...(transaction?.paymentInfos || []).map((v) => v.tenderSubtype),
    ]),
  };
};
